import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import PocLicenseBanner from './components/PocLicenseBanner';
import { Icon } from './components/ui';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AuditLogs from './pages/AuditLogs';
import Policies from './pages/Policies';
import AgentManagement from './pages/AgentManagement';
import MCPServers from './pages/MCPServers';
import McpGateway from './pages/McpGateway';
import LiveMcpFlow from './pages/LiveMcpFlow';
import Discovery from './pages/Discovery';
import EndpointAgents from './pages/EndpointAgents';
import LlmAccessPolicy from './pages/LlmAccessPolicy';
import Watermark from './pages/Watermark';
import JitAccess from './pages/JitAccess';
import Endpoints from './pages/Endpoints';
import DlpActivity from './pages/DlpActivity';
import DlpPolicy from './pages/DlpPolicy';
import AiAppPolicy from './pages/AiAppPolicy';
import ProviderCatalog from './pages/ProviderCatalog';
import Tripwire from './pages/Tripwire';
import AIGateway from './pages/AIGateway';
import AiCost from './pages/AiCost';
import Violations from './pages/Violations';
import Reports from './pages/Reports';
import Behaviour from './pages/Behaviour';
import Settings from './pages/Settings';
import License from './pages/License';
import ApiClients from './pages/ApiClients';
import ApprovalNotifier from './components/ApprovalNotifier';
// OnboardingApprovalPanel retired — its approve/reject workflow duplicated
// Agent Management's inline actions via a separate fallback-chain
// implementation. /onboarding/approval now redirects there instead (see route below).
import AgentPortalApp from './pages/AgentPortalApp';
import PocTestCases from './pages/PocTestCases';
import Approvals from './pages/Approvals';
import UserManagement from './pages/UserManagement';
import DirectorySettings from './pages/DirectorySettings';
import NetskopeSettings from './pages/NetskopeSettings';
import CommandCenter from './pages/CommandCenter';
import A2ATopics from './pages/a2a/A2ATopics';
import A2ATopicDetail from './pages/a2a/A2ATopicDetail';
import A2ADelegations from './pages/a2a/A2ADelegations';
import A2AProvenance from './pages/a2a/A2AProvenance';
import A2ADemo from './pages/a2a/A2ADemo';
import { keycloak, keycloakEnabled, samlEnabled } from './services/keycloak';
import { AuthContextProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useTranslation } from 'react-i18next';

// ── SAML fetch interceptor — installed at module scope so it is active
// before any component useEffect (React runs child effects before parent effects).
// Reads the token fresh from localStorage on every request; no React state needed.
// Always install a token-attaching fetch wrapper. When a `ztai-saml-token` is
// present (set by SAML/SSO *or* the local-IDM mock login used by POC stacks),
// attach it as a Bearer header so admin API calls authenticate; when absent it
// passes through untouched. Previously this was gated on `samlEnabled`, which
// left POC (local-IDM) logins with a stored token that was never sent -> 401s.
{
  const _nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = localStorage.getItem('ztai-saml-token');
    if (!token) return _nativeFetch(input, init);

    if (input instanceof Request) {
      const headers = new Headers(input.headers);
      if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      return _nativeFetch(new Request(input, { headers }), init);
    }

    const headers = new Headers(init?.headers || {});
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return _nativeFetch(input, { ...init, headers });
  };
}

function App() {
  const { t } = useTranslation();
  const [isConnected, setIsConnected] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    // Load SAML token from localStorage on mount (synchronously)
    if (samlEnabled) {
      return localStorage.getItem('ztai-saml-token');
    }
    return null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Restore auth state from localStorage on mount
    if (keycloakEnabled) {
      return false; // Keycloak handles this internally
    }
    if (samlEnabled) {
      // If we have a SAML token, we're authenticated
      return Boolean(localStorage.getItem('ztai-saml-token'));
    }
    return localStorage.getItem('ztai-auth') === 'true';
  });
  const [authReady, setAuthReady] = useState(() => {
    // Ready immediately if we have a token or using local auth
    if (keycloakEnabled) {
      return false; // Wait for Keycloak to initialize
    }
    if (samlEnabled) {
      // Ready if we have a stored SAML token
      return Boolean(localStorage.getItem('ztai-saml-token'));
    }
    return true;
  });
  const [isMobileNav, setIsMobileNav] = useState(() => window.innerWidth <= 920);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('ztai-theme');
    return saved ? saved === 'dark' : true;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('ztai-sidebar-collapsed');
    return saved ? saved === 'true' : false;
  });
  // Derive API URL at runtime: prefer env var (build-time baked), else use current origin on
  // same port as firewall (3000). When dashboard is on ztai.fidar.ai:3100, API requests go to
  // ztai.fidar.ai:3000 (localhost:3000 in dev). This respects the actual deployment hostname
  // without requiring a rebuild. For a different API host, set REACT_APP_API_URL at build time.
  const runtimeEnv = (window as any).__ZTAI_ENV__ || {};
  // Appliance: API_PORT makes the API follow the name the browser used for the dashboard.
  const apiUrl = (runtimeEnv.API_PORT && `${window.location.protocol}//${window.location.hostname}:${runtimeEnv.API_PORT}`) || runtimeEnv.API_URL || process.env.REACT_APP_API_URL || (() => {
    const host = window.location.hostname; // e.g. ztai.fidar.ai or localhost
    const protocol = window.location.protocol; // e.g. http: or https:
    // API is on the same host but port 3000 (not the dashboard port)
    return `${protocol}//${host}:3000`;
  })();

  // Apply dark mode class to root + persist preference
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('ztai-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Update sidebar width CSS variable and persist collapsed state
  useEffect(() => {
    const sidebarWidth = sidebarCollapsed ? '64px' : 'var(--sidebar-w-expanded, 260px)';
    document.documentElement.style.setProperty('--sidebar-w', sidebarWidth);
    localStorage.setItem('ztai-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleToggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 920;
      setIsMobileNav(mobile);
      if (!mobile) {
        setMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileSidebarOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileSidebarOpen]);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch(`${apiUrl}/health`);
        setIsConnected(response.ok);
      } catch (error) {
        setIsConnected(false);
        console.warn('Cannot connect to Agenti5 API:', error);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [apiUrl]);

  useEffect(() => {
    if (!keycloakEnabled) {
      return;
    }

    let mounted = true;

    keycloak
      .init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (!mounted) {
          return;
        }
        setIsAuthenticated(authenticated);
        setAccessToken(keycloak.token || null);
        setAuthReady(true);
      })
      .catch((error) => {
        console.error('Keycloak initialization failed:', error);
        if (mounted) {
          setIsAuthenticated(false);
          setAccessToken(null);
          setAuthReady(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!keycloakEnabled || !isAuthenticated) {
      return;
    }

    const refreshInterval = window.setInterval(async () => {
      try {
        const refreshed = await keycloak.updateToken(60);
        if (refreshed) {
          setAccessToken(keycloak.token || null);
        }
      } catch (error) {
        console.warn('Keycloak token refresh failed:', error);
        setIsAuthenticated(false);
        setAccessToken(null);
      }
    }, 30000);

    return () => window.clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Keycloak fetch interceptor: wraps the (possibly already SAML-wrapped) fetch
  // to refresh the OIDC token before each request.
  useEffect(() => {
    if (!keycloakEnabled) return;

    const prevFetch = window.fetch.bind(window);

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      if (!keycloak.authenticated) return prevFetch(input, init);
      try {
        await keycloak.updateToken(30);
      } catch (error) {
        console.warn('Unable to refresh Keycloak token before request:', error);
      }
      const token = keycloak.token;
      if (!token) return prevFetch(input, init);

      if (input instanceof Request) {
        const headers = new Headers(input.headers);
        if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
        return prevFetch(new Request(input, { headers }), init);
      }

      const headers = new Headers(init?.headers || {});
      if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      return prevFetch(input, { ...init, headers });
    };

    return () => { window.fetch = prevFetch; };
  }, []); // runs once; keycloak.token is read live on each request

  // ── Handle SAML callback: check for token in URL params after IdP redirect ──
  useEffect(() => {
    if (!samlEnabled) return;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setAccessToken(token);
      setIsAuthenticated(true);
      setAuthReady(true);
      localStorage.setItem('ztai-auth', 'true');
      localStorage.setItem('ztai-saml-token', token);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // Token already restored synchronously in useState initializer — just mark auth ready
      setAuthReady(true);
    }
  }, []);

  const handleLogin = async () => {
    if (keycloakEnabled) {
      await keycloak.login({
        redirectUri: `${window.location.origin}/dashboard`,
      });
      return;
    }

    if (samlEnabled) {
      // Redirect to firewall SAML login endpoint with relay back to dashboard
      const samlLoginUrl = `${apiUrl}/auth/saml/login?RelayState=${encodeURIComponent(window.location.origin + '/dashboard')}`;
      window.location.href = samlLoginUrl;
      return;
    }

    localStorage.setItem('ztai-auth', 'true');
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    // Record the sign-out to the admin audit trail (best-effort, fire-and-forget)
    // while the token is still present so the request authenticates.
    try {
      const hasToken = Boolean(localStorage.getItem('ztai-saml-token'));
      if (hasToken) {
        fetch(`${apiUrl}/api/admin/user/logout`, { method: 'POST' }).catch(() => {});
      }
    } catch { /* never block logout on audit */ }

    if (keycloakEnabled) {
      keycloak.logout({
        redirectUri: `${window.location.origin}/login`,
      });
      return;
    }

    if (samlEnabled) {
      localStorage.removeItem('ztai-saml-token');
    }

    localStorage.removeItem('ztai-auth');
    setAccessToken(null);
    setIsAuthenticated(false);
  };

  return (
    <BrowserRouter>
      <AuthContextProvider
        isAuthenticated={isAuthenticated}
        accessToken={accessToken}
        authReady={authReady}
        apiUrl={apiUrl}
      >
        {!authReady ? null : (
        <>
        {/* ── Landing Page (Home) ── */}
        <Routes>
        <Route 
          path="/" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing isDarkMode={isDarkMode} onToggleDarkMode={() => setIsDarkMode(!isDarkMode)} />}
        />

        {/* ── Login Page ── */}
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onLogin={handleLogin} useKeycloak={keycloakEnabled} useSaml={samlEnabled} isDarkMode={isDarkMode} onToggleDarkMode={() => setIsDarkMode(!isDarkMode)} />}
        />

        {/* ── Standalone Agent Portal (no sidebar) ── */}
        <Route path="/agent-portal/*" element={<AgentPortalApp apiUrl={apiUrl} />} />

        {/* ── Main dashboard app (with sidebar) ── */}
        <Route
          element={
            isAuthenticated ? (
              <div className="app-container">
                {isMobileNav && mobileSidebarOpen && <div className="mobile-sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} aria-hidden="true" />}
                <Sidebar
                  isConnected={isConnected}
                  isDarkMode={isDarkMode}
                  onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
                  onLogout={handleLogout}
                  collapsed={isMobileNav ? false : sidebarCollapsed}
                  onToggleCollapse={handleToggleSidebarCollapse}
                  showCollapseToggle={!isMobileNav}
                  mobileOpen={mobileSidebarOpen}
                  onNavigate={() => {
                    if (isMobileNav) {
                      setMobileSidebarOpen(false);
                    }
                  }}
                />
                <ApprovalNotifier apiUrl={apiUrl} />
                <div className="main-content">
                  <PocLicenseBanner />
                  {isMobileNav && (
                    <div className="mobile-topbar">
                      <button
                        className="mobile-menu-btn"
                        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                        aria-label={mobileSidebarOpen ? t('app.closeNavigationMenu') : t('app.openNavigationMenu')}
                      >
                        <Icon name={mobileSidebarOpen ? 'close' : 'chevronRight'} mirrorInRtl />
                        <span>{t('app.menu')}</span>
                      </button>
                    </div>
                  )}
                  {!isConnected && (
                    <div className="connection-banner animate-pulse">
                      <Icon name="warning" /> {t('app.cannotConnectApi', { url: apiUrl })}
                    </div>
                  )}
                  <Outlet />
                </div>
              </div>
            ) : <Navigate to="/" replace />
          }
        >
          <Route path="/dashboard" element={<Dashboard apiUrl={apiUrl} />} />
          <Route path="/audit-logs" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:audit-logs" element={<AuditLogs apiUrl={apiUrl} />} />} />
          <Route path="/policies" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<Policies apiUrl={apiUrl} />} />} />
          <Route path="/agents" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<AgentManagement apiUrl={apiUrl} />} />} />
          <Route path="/mcp-servers" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:mcp-servers" element={<MCPServers apiUrl={apiUrl} />} />} />
          <Route path="/mcp-gateway" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:mcp-servers" element={<McpGateway apiUrl={apiUrl} />} />} />
          <Route path="/mcp-flow" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:mcp-servers" element={<LiveMcpFlow apiUrl={apiUrl} />} />} />
          <Route path="/discovery" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:mcp-servers" element={<Discovery apiUrl={apiUrl} />} />} />
          <Route path="/endpoint-agents" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<EndpointAgents apiUrl={apiUrl} />} />} />
          {/* Access Control — which endpoints/users may reach which AI services
              (standing rules), and time-bound access to them (JIT). */}
          <Route path="/watermark" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<Watermark apiUrl={apiUrl} />} />} />
          <Route path="/llm-access-policy" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<LlmAccessPolicy apiUrl={apiUrl} />} />} />
          <Route path="/jit-access" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<JitAccess apiUrl={apiUrl} />} />} />
          <Route path="/endpoints" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<Endpoints apiUrl={apiUrl} />} />} />
          <Route path="/dlp-activity" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<DlpActivity apiUrl={apiUrl} />} />} />
          <Route path="/dlp-policy" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<DlpPolicy apiUrl={apiUrl} />} />} />
          <Route path="/ai-app-policy" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<AiAppPolicy apiUrl={apiUrl} />} />} />
          <Route path="/provider-catalog" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:policies" element={<ProviderCatalog apiUrl={apiUrl} />} />} />
          <Route path="/tripwire" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<Tripwire apiUrl={apiUrl} />} />} />
          <Route path="/ai-gateway" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:mcp-servers" element={<AIGateway apiUrl={apiUrl} />} />} />
          <Route path="/ai-cost" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:dashboard" element={<AiCost apiUrl={apiUrl} />} />} />
          <Route path="/violations" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<Violations apiUrl={apiUrl} />} />} />
          <Route path="/command-center" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<CommandCenter apiUrl={apiUrl} />} />} />
          <Route path="/a2a/topics" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<A2ATopics apiUrl={apiUrl} />} />} />
          <Route path="/a2a/topics/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<A2ATopicDetail apiUrl={apiUrl} />} />} />
          <Route path="/a2a/delegations" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<A2ADelegations apiUrl={apiUrl} />} />} />
          <Route path="/a2a/provenance" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:audit-logs" element={<A2AProvenance apiUrl={apiUrl} />} />} />
          <Route path="/a2a/demo" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:agents" element={<A2ADemo apiUrl={apiUrl} />} />} />
          <Route path="/approvals" element={<Approvals apiUrl={apiUrl} />} />
          <Route path="/users" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:users" element={<UserManagement apiUrl={apiUrl} />} />} />
          <Route path="/netskope" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:settings" element={<NetskopeSettings apiUrl={apiUrl} />} />} />
          <Route path="/directory" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:settings" element={<DirectorySettings apiUrl={apiUrl} />} />} />
          <Route path="/reports" element={<Reports apiUrl={apiUrl} />} />
          <Route path="/behaviour" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="view:violations" element={<Behaviour apiUrl={apiUrl} />} />} />
          <Route path="/guided-tests" element={<PocTestCases apiUrl={apiUrl} />} />
          <Route path="/api-clients" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:settings" element={<ApiClients apiUrl={apiUrl} />} />} />
          <Route path="/license" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:settings" element={<License apiUrl={apiUrl} />} />} />
          <Route path="/settings" element={<ProtectedRoute isAuthenticated={isAuthenticated} requiredPermission="manage:settings" element={<Settings apiUrl={apiUrl} />} />} />
          <Route path="/onboarding/approval" element={<Navigate to="/agents" replace />} />
        </Route>
      </Routes>
      </>
      )}
      </AuthContextProvider>
    </BrowserRouter>
  );
}

export default App;
